import { Check } from "lucide-react";

import { useCheckUser } from "@/api/user/check-user";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Navigate } from "react-router";
import useGlobalLoading from "@/store/global-loading";
import { useEffect } from "react";
import useOnboardingData from "@/store/onboarding-data";
import { Button } from "@/components/ui/button";
import { useGetAllFeaturePlans } from "@/api/plan/get-all-feature-plans";
import { useCreateSubscription } from "@/api/billing/create-subscription";
import { useGetMySubscription } from "@/api/billing/get-my-subscription";
import { usePaidUser } from "@/api/onboarding/user-is-paid";

// Define a type for our master features
type FeatureKey =
    | "ANALYTICS_REPORTS"
    | "BRANDING_WATERMARK"
    | "AGENT_CHAT_SEATS"
    | "KNOWLEDGE_BASE_PDFS"
    | "ACTION_BUTTONS_COUNT"
    | "PRODUCT_SUPPORT_ITEMS"
    | "SOCIAL_MEDIA_INTEGRATION"
    | "ON_SITE_TICKETING_LIMIT"
    | "SUPPORT_RESPONSE_TIME"
    | "LEAD_EXPORT"
    | "LEAD_RETARGETING"
    | "LANGUAGE_SUPPORT_COUNT"
    | "EMBEDDING_SITES_COUNT"
    | "CODE_CUSTOMIZATION"
    | "ORDER_PLACEMENT"
    | "RETURN_REFUND_REPLACEMENT";

// Master list of all features that will appear on every card
const allFeaturesConfig: { key: FeatureKey; label: string }[] = [
    { key: "ANALYTICS_REPORTS", label: "Analytics & Reports" },
    { key: "BRANDING_WATERMARK", label: "Chatbot Branding" },
    { key: "AGENT_CHAT_SEATS", label: "AI & Live Agent Seats" },
    { key: "KNOWLEDGE_BASE_PDFS", label: "Knowledge Base PDFs" },
    { key: "ACTION_BUTTONS_COUNT", label: "Custom Action Buttons" },
    { key: "PRODUCT_SUPPORT_ITEMS", label: "Product Catalog Size" },
    { key: "SOCIAL_MEDIA_INTEGRATION", label: "Social Media Integration" },
    { key: "ON_SITE_TICKETING_LIMIT", label: "On-Site Support Ticketing" },
    { key: "SUPPORT_RESPONSE_TIME", label: "Priority Support Response" },
    { key: "LEAD_EXPORT", label: "Lead Export" },
    { key: "LEAD_RETARGETING", label: "Lead Retargeting" },
    { key: "LANGUAGE_SUPPORT_COUNT", label: "Multi-Language Support" },
    { key: "EMBEDDING_SITES_COUNT", label: "Website Embedding Count" },
    { key: "CODE_CUSTOMIZATION", label: "API & Code Customization" },
    { key: "ORDER_PLACEMENT", label: "Direct Order Placement" },
    { key: "RETURN_REFUND_REPLACEMENT", label: "Automated Returns & Refunds" },
];

const StepThree = () => {
    const { data: userData } = useCheckUser();
    const { mutate: userPaid } = usePaidUser();
    const { setIsLoading } = useGlobalLoading();
    const { isStep3Complete, storeData } = useOnboardingData();
    const { data: featurePlansData, isLoading: plansLoading, error } = useGetAllFeaturePlans();
    const { data: mySubscriptionData, isLoading: mySubscriptionLoading } = useGetMySubscription(storeData?.id || "");
    const createSubscriptionMutation = useCreateSubscription();
    
    const handlePlan = (planId: string | number) => {
        if (storeData?.id) {
            createSubscriptionMutation.mutate({
                store_id: storeData.id,
                plan_id: String(planId),
            });
        } else {
            console.error("No store selected for subscription");
        }
        setIsLoading(true);
    };

    useEffect(() => {
        if (mySubscriptionLoading) {
            setIsLoading(true);
        }
    }, [mySubscriptionLoading, setIsLoading]);

    // Check if user already has a subscription on component mount
    useEffect(() => {
        if (mySubscriptionData && !mySubscriptionData.meta.error && mySubscriptionData.data && mySubscriptionData.data.is_active) {
            // User already has an active subscription, mark as paid and navigate to dashboard
            userPaid();
        }
    }, [mySubscriptionData, userPaid]);

    if ((userData?.meta?.code === 200 && userData?.meta?.message === "Store and User found" && userData?.data?.user?.is_paid && isStep3Complete) || 
        (mySubscriptionData && !mySubscriptionData.meta.error && mySubscriptionData.data && mySubscriptionData.data.is_active)) {
        return <Navigate to="/app/dashboard" replace />;
    }

    if (userData?.data?.user?.role === "member") {
        return <Navigate to="/app/dashboard" replace />;
    }

    if (plansLoading) {
        return (
            <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h1 className="text-4xl lg:text-5xl font-extrabold text-gray-900">Choose your right plan!</h1>
                        <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
                            Loading available plans...
                        </p>
                    </div>
                    <div className="flex justify-center items-center h-64">
                        <div className="text-lg text-gray-600">Loading plans...</div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h1 className="text-4xl lg:text-5xl font-extrabold text-gray-900">Choose your right plan!</h1>
                        <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
                            Error loading plans. Please try again later.
                        </p>
                    </div>
                    <div className="flex justify-center items-center h-64">
                        <div className="text-lg text-red-600">Error loading plans</div>
                    </div>
                </div>
            </div>
        );
    }

    const plans = featurePlansData?.data || [];

    return (
        // <div className={cn("mx-auto flex min-h-[calc(100vh-200px)] max-w-[calc(100vw-35rem)] flex-col justify-center text-gray-700")}>
        //     <h3 className="text-center text-2xl font-semibold">Price Plan</h3>
        //     <p className="mt-2 text-center font-medium">Choose the best plan for your business. Change plans whenever you need to.</p>
        //     <div className="mb-10 mt-6">
        //         <div className="flex items-center justify-between">
        //             <div className="relative z-20 flex items-center justify-center gap-2 rounded-full border border-blue-500 bg-white p-2 text-blue-700">
        //                 <div className="rounded-full bg-blue-500 text-white">
        //                     <CircleCheck />
        //                 </div>
        //                 <p>Step 1</p>
        //             </div>
        //             <div className="absolute w-[calc(50%-17.5rem)] min-w-11 border border-blue-500"></div>
        //             <div className="relative z-20 flex items-center justify-center gap-2 rounded-full border border-blue-500 bg-white p-2 text-blue-700">
        //                 <div className="rounded-full bg-blue-500 text-white">
        //                     <CircleCheck />
        //                 </div>
        //                 <p>Step 2</p>
        //             </div>
        //             <div className="absolute left-[49%] w-[calc(50%-17.5rem)] min-w-11 border border-blue-500"></div>
        //             <div className="relative z-20 flex items-center justify-center gap-2 rounded-full border border-blue-500 bg-white p-2 text-blue-700">
        //                 <CircleCheck />
        //                 <p>Step 3</p>
        //             </div>
        //         </div>
        //     </div>
        //     <Card className="flex justify-evenly gap-4 border p-4">
        //         <Card className="w-full cursor-pointer p-4 shadow-none hover:border-blue-700" onClick={handlePlan}>
        //             <h3 className="text-center text-xl font-bold">Starter</h3>
        //             <Separator className="my-4" />
        //             <ol className="list-inside list-decimal">
        //                 <li>Feature 1</li>
        //                 <li>Feature 2</li>
        //                 <li>Feature 3</li>
        //                 <li>Feature 4</li>
        //                 <li className="text-gray-500">Feature 5</li>
        //                 <li className="text-gray-500">Feature 6</li>
        //                 <li className="text-gray-500">Feature 7</li>
        //                 <li className="text-gray-500">Feature 8</li>
        //             </ol>
        //         </Card>
        //         <Card className="w-full cursor-pointer p-4 shadow-none hover:border-blue-700" onClick={handlePlan}>
        //             <h3 className="text-center text-xl font-bold">Growth</h3>
        //             <Separator className="my-4" />
        //             <ol className="list-inside list-decimal">
        //                 <li>Feature 1</li>
        //                 <li>Feature 2</li>
        //                 <li>Feature 3</li>
        //                 <li>Feature 4</li>
        //                 <li>Feature 5</li>
        //                 <li className="text-gray-500">Feature 6</li>
        //                 <li className="text-gray-500">Feature 7</li>
        //                 <li className="text-gray-500">Feature 8</li>
        //             </ol>
        //         </Card>
        //         <Card className="w-full cursor-pointer p-4 shadow-none hover:border-blue-700" onClick={handlePlan}>
        //             <h3 className="text-center text-xl font-bold">Pro</h3>
        //             <Separator className="my-4" />
        //             <ol className="list-inside list-decimal">
        //                 <li>Feature 1</li>
        //                 <li>Feature 2</li>
        //                 <li>Feature 3</li>
        //                 <li>Feature 4</li>
        //                 <li>Feature 5</li>
        //                 <li>Feature 6</li>
        //                 <li className="text-gray-500">Feature 7</li>
        //                 <li className="text-gray-500">Feature 8</li>
        //             </ol>
        //         </Card>
        //         <Card className="w-full cursor-pointer p-4 shadow-none hover:border-blue-700" onClick={handlePlan}>
        //             <h3 className="text-center text-xl font-bold">Scale</h3>
        //             <Separator className="my-4" />
        //             <ol className="list-inside list-decimal">
        //                 <li>Feature 1</li>
        //                 <li>Feature 2</li>
        //                 <li>Feature 3</li>
        //                 <li>Feature 4</li>
        //                 <li>Feature 5</li>
        //                 <li>Feature 6</li>
        //                 <li>Feature 7</li>
        //                 <li>Feature 8</li>
        //             </ol>
        //         </Card>
        //     </Card>
        // </div>
        <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                    <h1 className="text-4xl lg:text-5xl font-extrabold text-gray-900">Choose your right plan!</h1>
                    <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
                        Select from the best plans, ensuring a perfect match for your needs.
                    </p>
                </div>

                {/* Monthly/Quarterly Switch removed */}
                {/* <div className="flex justify-center items-center my-10"> ... </div> */}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch pt-10">
                    {" "}
                    {/* Changed lg:grid-cols-4 to lg:grid-cols-3 */}
                    {plans.map((plan: any) => (
                            <Card
                                key={plan.plan_id}
                                className={`flex flex-col rounded-xl shadow-xl transition-all duration-300 transform hover:scale-105 
                ${plan.is_active ? "border-purple-500 border-2 relative" : "border-gray-200"}
                bg-white`} // isCustom check removed for background
                            >
                                {plan.is_active && (
                                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-purple-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md">
                                        POPULAR
                                    </div>
                                )}
                                <CardHeader className="p-6 text-center">
                                    <CardTitle className={`text-2xl font-bold text-purple-700`}>{plan.plan_name}</CardTitle>{" "}
                                    {/* isCustom check removed for color */}
                                    {/* Price display simplified as custom plan is removed */}
                                    <div className="mt-4">
                                        <span className={`text-5xl font-extrabold text-gray-900`}>${plan.plan_price}</span>
                                        <span className={`text-lg font-medium text-gray-500`}>/month</span>
                                    </div>
                                    <CardDescription className={`mt-3 text-sm text-gray-600 min-h-[3em]`}>
                                        {" "}
                                        {/* isCustom check removed for color */}
                                        {plan.plan_description}
                                    </CardDescription>
                                </CardHeader>
                            <CardContent className="p-6 pt-0 flex-grow">
                                <ul className="space-y-3">
                                    {allFeaturesConfig.map((masterFeature) => {
                                        // Since features aren't available directly in this API response,
                                        // we'll show all features as active as a placeholder to avoid type issues
                                        // In a real implementation, you'd fetch features separately
                                        const isActive = true;

                                        return (
                                            <li key={masterFeature.key} className="flex items-start">
                                                <Check
                                                    className={`w-5 h-5 mr-3 flex-shrink-0 mt-1 ${isActive ? "text-purple-600" : "text-gray-400"}`}
                                                />
                                                <span className={`text-sm ${isActive ? "text-gray-700" : "text-gray-400"}`}>
                                                    {masterFeature.label}
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </CardContent>
                            <div className="p-6 pt-2 mt-auto">
                                {" "}
                                <Button
                                    onClick={() => handlePlan(Number(plan.plan_id))}
                                    className={`w-full text-lg font-semibold py-3 rounded-md cursor-pointer
                        bg-purple-600 text-white hover:bg-purple-700`}
                                    variant={"default"}
                                    disabled={createSubscriptionMutation.isPending}
                                >
                                    {createSubscriptionMutation.isPending && createSubscriptionMutation.variables?.plan_id === plan.plan_id
                                        ? "Processing..."
                                        : "Get Started"}
                                </Button>
                            </div>
                        </Card>
                    ))}
                </div>

                <div className={`mt-16 text-center text-gray-700`}>
                    <h2 className="text-3xl font-bold mb-6">Language Support Details</h2>
                    <p className="max-w-3xl mx-auto text-md leading-relaxed">
                        Our GDN AI ChatBot offers extensive language capabilities. We support the following 10 languages:
                        <span className="font-semibold">
                            {" "}
                            English, Mandarin, Arabic, Bangla, Hindi, French, Spanish, German, Portuguese, and Russian.
                        </span>
                        <br />
                        <br />
                        These languages cover a significant portion of the global population, ensuring your chatbot can communicate effectively with a
                        diverse customer base, whether as a native or second language.
                    </p>
                    <p className="mt-8 font-semibold text-lg">
                        For businesses with unique needs beyond these plans, we still offer tailored solutions.
                        <br />
                        Contact us for a custom quote. We also provide Reskin of the Whole System if it is needed.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default StepThree;
